/* OFX collection — mobile filter bottom-sheet (≤989px).
   Progressive enhancement over today's stacked sidebar (the no-JS fallback). With
   JS off, this file never runs and the filter aside renders inline, fully usable.

   What it does, at mobile widths only (matchMedia ≤989px):
   - Treats the .ofx-coll__sheet wrapper as a slide-up dialog. The same filter form
     nodes (incl. the single price slider) are MOVED into the sheet by CSS — no clone,
     no second slider — so ofx-coll-price-slider.js still finds exactly one instance.
   - Open: .is-open + scroll-lock (theme convention: html[scroll-lock]); strips the
     closed-state inert/aria-hidden; moves focus into the panel; Filter btn aria-expanded.
   - Close: re-applies inert + aria-hidden; unlocks scroll; restores focus to the
     Filter button. Backdrop click, Esc, and the ✕ all close.
   - Focus trap inside the open panel (mirrors header-drawer behaviour).
   - Toolbar gains .is-stuck (shadow) once it pins to the top.
   - Above 989px the sheet is neutralised (no inert/aria-hidden/is-open, scroll
     unlocked) so the desktop display:contents sidebar stays fully interactive.

   prefers-reduced-motion is honoured by CSS (transitions disabled); this script does
   no motion of its own.
*/
(function () {
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initSection(root) {
    if (root.__ofxSheetInit) return;
    root.__ofxSheetInit = true;

    var sheet = root.querySelector('.ofx-coll__sheet');
    var panel = root.querySelector('.ofx-coll__sheet-panel');
    var openBtn = root.querySelector('.ofx-coll__filter-btn');
    var toolbar = root.querySelector('.ofx-coll__toolbar');
    if (!sheet || !panel || !openBtn) return;

    var mq = window.matchMedia('(max-width: 989px)');
    var isOpen = false;
    var labelledby = panel.getAttribute('data-ofx-sheet-labelledby');

    // ---- dialog role (only when the panel actually behaves as a dialog: mobile) ----
    function applyDialogRole() {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      if (labelledby) panel.setAttribute('aria-labelledby', labelledby);
    }
    function clearDialogRole() {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('aria-labelledby');
    }

    // ---- closed-state attributes (only meaningful at mobile) ----
    function applyClosedAttrs() {
      sheet.setAttribute('aria-hidden', 'true');
      sheet.setAttribute('inert', '');
    }
    function clearClosedAttrs() {
      sheet.removeAttribute('aria-hidden');
      sheet.removeAttribute('inert');
    }

    function open() {
      if (isOpen || !mq.matches) return;
      isOpen = true;
      clearClosedAttrs();
      sheet.classList.add('is-open');
      document.documentElement.setAttribute('scroll-lock', '');
      openBtn.setAttribute('aria-expanded', 'true');
      // Focus the first focusable in the panel (the ✕), falling back to the panel.
      var first = panel.querySelector('.ofx-coll__sheet-close') || panel.querySelector(FOCUSABLE);
      if (first) {
        first.focus();
      } else {
        panel.setAttribute('tabindex', '-1');
        panel.focus();
      }
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      sheet.classList.remove('is-open');
      document.documentElement.removeAttribute('scroll-lock');
      openBtn.setAttribute('aria-expanded', 'false');
      if (mq.matches) applyClosedAttrs();
      openBtn.focus();
    }

    openBtn.addEventListener('click', open);

    root.querySelectorAll('[data-ofx-sheet-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close();
      }
    });

    // ---- focus trap within the open panel ----
    panel.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isOpen) return;
      var f = panel.querySelectorAll(FOCUSABLE);
      var visible = [];
      for (var i = 0; i < f.length; i++) {
        // offsetParent null => not rendered (e.g. dim/disabled rows already excluded)
        if (f[i].offsetParent !== null || f[i] === document.activeElement) visible.push(f[i]);
      }
      if (!visible.length) return;
      var firstEl = visible[0];
      var lastEl = visible[visible.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    });

    // ---- toolbar sticky shadow ----
    // The toolbar pins BELOW the sticky header (top: var(--sticky-h)) on
    // collection templates, so it reaches its pinned position at y≈--sticky-h,
    // not 0. Compare its top to the live pinned-header height (the header JS
    // writes --sticky-h on :root) so the shadow fires the moment it tucks
    // beneath the header — matching collections-mobile.html. Fallback 0 keeps
    // the original behaviour if --sticky-h is ever unset (non-sticky context).
    if (toolbar) {
      window.addEventListener('scroll', function () {
        if (!mq.matches) {
          toolbar.classList.remove('is-stuck');
          return;
        }
        var stickyH = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--sticky-h')
        ) || 0;
        toolbar.classList.toggle('is-stuck', toolbar.getBoundingClientRect().top <= stickyH + 1);
      }, { passive: true });
    }

    // ---- breakpoint sync ----
    function syncToBreakpoint() {
      if (mq.matches) {
        // Mobile: the panel is a dialog. If closed, ensure the closed-state
        // attributes are present.
        applyDialogRole();
        if (!isOpen) applyClosedAttrs();
      } else {
        // Desktop: neutralise everything so the display:contents sidebar is live
        // and carries no dialog/closed semantics.
        isOpen = false;
        sheet.classList.remove('is-open');
        clearClosedAttrs();
        clearDialogRole();
        document.documentElement.removeAttribute('scroll-lock');
        openBtn.setAttribute('aria-expanded', 'false');
        if (toolbar) toolbar.classList.remove('is-stuck');
      }
    }
    if (mq.addEventListener) mq.addEventListener('change', syncToBreakpoint);
    else if (mq.addListener) mq.addListener(syncToBreakpoint);

    syncToBreakpoint();
  }

  function initAll() {
    var roots = document.querySelectorAll('.ofx-coll');
    for (var i = 0; i < roots.length; i++) initSection(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init after Shopify section re-renders (theme editor / Section Rendering API).
  document.addEventListener('shopify:section:load', initAll);
})();
